import React, { useState, useEffect } from 'react';
import { CampaignCard } from './CampaignCard';
import { CampaignDetailsModal } from './CampaignDetailsModal';
import { MatchApplicationForm } from '../matches/MatchApplicationForm';
import { Spinner } from '../common/Spinner';
import { graphqlApi } from '../../services/graphql';
import { Campaign } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface CampaignFilters {
  category?: string;
  searchTerm?: string;
  minBudget?: number;
  maxBudget?: number;
  status?: string;
}

const CAMPAIGN_CATEGORIES = [
  'all',
  'Fashion & Beauty',
  'Travel & Hospitality',
  'Food & Beverage',
  'Technology',
  'Health & Fitness',
  'Lifestyle',
  'Gaming',
  'Education',
  'Finance',
  'Other'
];

export const CampaignDiscovery: React.FC = () => {
  const { user } = useAuthStore();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showQuickApplicationForm, setShowQuickApplicationForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<CampaignFilters>({
    category: 'all',
    searchTerm: '',
    status: 'active'
  });

  useEffect(() => {
    fetchCampaigns();
  }, [filters]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      // Build filter object for GraphQL query - only fetch active campaigns for discovery
      const queryFilters: any = {
        status: 'active' // Only show active campaigns for discovery
      };

      if (filters.category && filters.category !== 'all') {
        queryFilters.category = filters.category;
      }

      const response = await graphqlApi.getCampaigns(queryFilters);

      if (response.success) {
        const baseCampaignData = response.data?.campaigns || [];

        // Apply client-side search filter if needed
        const campaignData = filters.searchTerm ?
          baseCampaignData.filter((campaign: Campaign) => {
            const searchLower = filters.searchTerm!.toLowerCase();
            return (
              (campaign.campaignName || campaign.title || '').toLowerCase().includes(searchLower) ||
              (campaign.productIntroduction || campaign.description || '').toLowerCase().includes(searchLower) ||
              (campaign.campaignCategory || campaign.category || '').toLowerCase().includes(searchLower)
            );
          }) : baseCampaignData;

        setCampaigns(campaignData);
      } else {
        toast.error(response.error || 'Failed to fetch campaigns');
      }
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      toast.error(error.message || 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailsModal(true);
  };

  const handleQuickApply = (campaign: Campaign) => {
    if (!user || user.userType !== 'influencer') {
      toast.error('You must be logged in as an influencer to apply');
      return;
    }
    setSelectedCampaign(campaign);
    setShowQuickApplicationForm(true);
  };

  const handleApplicationSubmit = (matchId: string) => {
    setShowQuickApplicationForm(false);
    setSelectedCampaign(null);
    toast.success('Application submitted successfully!');
    // Optionally refresh campaigns to update application counts
    fetchCampaigns();
  };

  const handleFilterChange = (key: keyof CampaignFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: 'all',
      searchTerm: '',
      status: 'active'
    });
  };

  const isInfluencer = user?.userType === 'influencer';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Discover Campaigns</h1>
          <p className="mt-2 text-gray-600">
            {isInfluencer
              ? 'Find and apply to exciting campaign opportunities'
              : 'Browse available campaigns (login as an influencer to apply)'
            }
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                />
              </div>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              Filters
              {filters.category !== 'all' && (
                <span className="ml-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                  1
                </span>
              )}
            </button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                  >
                    {CAMPAIGN_CATEGORIES.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {loading ? 'Loading...' : `${campaigns.length} campaign${campaigns.length !== 1 ? 's' : ''} available`}
          </p>
        </div>

        {/* Campaign Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spinner />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No campaigns found
            </h3>
            <p className="text-gray-600">
              {filters.searchTerm || filters.category !== 'all'
                ? 'Try adjusting your search criteria or clearing filters'
                : 'No active campaigns are available at the moment'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <CampaignCard
                key={campaign.campaignId}
                campaign={campaign}
                onViewDetails={handleViewDetails}
                onApply={handleQuickApply}
                showApplyButton={isInfluencer}
              />
            ))}
          </div>
        )}

        {/* Campaign Details Modal */}
        {selectedCampaign && (
          <CampaignDetailsModal
            campaign={selectedCampaign}
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedCampaign(null);
            }}
          />
        )}

        {/* Quick Application Form Modal */}
        {showQuickApplicationForm && selectedCampaign && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                onClick={() => {
                  setShowQuickApplicationForm(false);
                  setSelectedCampaign(null);
                }}
              />
              <div className="relative inline-block w-full max-w-2xl p-6 my-8 text-left transition-all transform bg-white rounded-lg shadow-xl sm:align-middle">
                <MatchApplicationForm
                  campaign={selectedCampaign}
                  onSubmit={handleApplicationSubmit}
                  onCancel={() => {
                    setShowQuickApplicationForm(false);
                    setSelectedCampaign(null);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
